import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

// Define the widgets you want to display
const widgets = [
  {
    id: 'slider-widget',
    type: 'iframe',
    src: "https://sotellus.com/widgets/stu-react-reviews-slider/index.html?uid=the-financial-er",
    height: "480px",
    title: "Sotellus Reviews Slider"
  }
  // Removed the second widget from here, so only the first one remains
  // {
  //   id: 'single-review-widget',
  //   type: 'div-embed', // This type will handle the script injection
  //   htmlContainerId: 'sotellus-single-review-container', // Unique ID for the div where the widget will render
  //   scriptSrc: "https://sotellus.com/widgets/stu-react-reviews-single/assets/stu-generate-iframe-multiple.js?uid=the-financial-er",
  //   title: "Sotellus Single Review"
  // }
];

const TestimonialCarousel = () => {
  const [currentWidgetIndex, setCurrentWidgetIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true); // New loading state
  const [error, setError] = useState(null); // New error state
  const scriptContainerRef = useRef(null); // Ref for the div-embed container

  // The nextWidget and prevWidget functions are still here,
  // as they might be used by the dot navigation or other future controls.
  const nextWidget = () => {
    setIsLoading(true); // Set loading true when changing widget
    setError(null); // Clear any previous errors
    setCurrentWidgetIndex((prev) => (prev + 1) % widgets.length);
  };

  const prevWidget = () => {
    setIsLoading(true); // Set loading true when changing widget
    setError(null); // Clear any previous errors
    setCurrentWidgetIndex((prev) => (prev - 1 + widgets.length) % widgets.length);
  };

  // Effect to handle script injection for 'div-embed' type widgets
  useEffect(() => {
    const currentWidget = widgets[currentWidgetIndex];
    setIsLoading(true); // Start loading for the new widget

    // Clear previous content and scripts from the scriptContainerRef
    if (scriptContainerRef.current) {
      scriptContainerRef.current.innerHTML = '';
      // Remove any script elements that might have been added to body by previous widget
      // Note: This might need more robust cleanup if scripts are added to head or body directly by the widget
      const scriptsToRemove = document.querySelectorAll(`script[src="${currentWidget.scriptSrc}"]`);
      scriptsToRemove.forEach(script => script.remove());
    }

    if (currentWidget.type === 'iframe') {
      // For iframes, we need to wait for the iframe to load its content
      // The iframe itself is rendered in the JSX, so we attach onload/onerror there
      // For now, we just set loading to false after a short delay if no iframe is found immediately
      const iframeElement = document.querySelector(`iframe[title="${currentWidget.title}"]`);
      if (iframeElement) {
        iframeElement.onload = () => setIsLoading(false);
        iframeElement.onerror = () => {
          setIsLoading(false);
          setError("Failed to load iframe content.");
        };
      } else {
        // Fallback if iframe not immediately found (e.g., due to React rendering cycle)
        setTimeout(() => setIsLoading(false), 500); 
      }
    } else if (currentWidget.type === 'div-embed') {
      if (scriptContainerRef.current) {
        scriptContainerRef.current.innerHTML = `<p class="text-gray-700 text-center">Loading single review...</p>`;

        const scriptElement = document.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.async = true;
        scriptElement.src = currentWidget.scriptSrc;

        // This part is crucial for Sotellus widgets
        const initScript = document.createElement('script');
        initScript.type = 'text/javascript';
        initScript.innerHTML = `
          window['__be_single_the-financial-er'] = window.__be || {};
          window['__be_single_the-financial-er'].id = "the-financial-er";
        `;
        
        scriptContainerRef.current.appendChild(initScript);
        scriptContainerRef.current.appendChild(scriptElement);

        scriptElement.onload = () => {
          setIsLoading(false);
        };
        scriptElement.onerror = (e) => {
          setIsLoading(false);
          setError("Failed to load review script.");
          console.error(`Error loading script for ${currentWidget.id}:`, e);
        };
      } else {
        setIsLoading(false);
        setError("Widget container not found."); 
      }
    }

    // Cleanup function
    return () => {
      if (currentWidget.type === 'div-embed' && scriptContainerRef.current) {
        scriptContainerRef.current.innerHTML = '';
        const scriptsToRemove = document.querySelectorAll(`script[src="${currentWidget.scriptSrc}"]`);
        scriptsToRemove.forEach(script => script.remove());
      }
    };
  }, [currentWidgetIndex]); // Dependency array ensures effect runs when currentWidgetIndex changes

  const currentWidget = widgets[currentWidgetIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12" /* Restored background and blur */
      style={{
        boxShadow: '0 0 20px rgba(255, 165, 0, 0.7), 0 0 40px rgba(255, 165, 0, 0.5)' // Orange glow
      }}
    >
      {/* Subtle Turquoise-Green Neon Glow from behind */}
      <motion.div
        className="absolute inset-0 rounded-3xl"
        style={{
          background: 'radial-gradient(circle at center, rgba(0, 255, 255, 0.2) 0%, rgba(0, 255, 255, 0) 70%)', // Turquoise-green glow
          filter: 'blur(30px)', // Very blurry for a soft aura
          zIndex: -1, // Behind the main content
        }}
        animate={{
          opacity: [0.1, 0.3, 0.2, 0.4, 0.1], // Varying opacity for flicker
          scale: [1, 1.05, 0.95, 1.02, 1], // Subtle scale for breathing effect
        }}
        transition={{
          duration: 5, // Flicker cycle duration
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "mirror", // Go back and forth
        }}
      />

      <h2 className="relative z-10 text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-800 mb-8 text-center">
        Success Stories
      </h2>
      
      <div className="relative z-10 flex justify-center items-center min-h-[500px] overflow-hidden">
        {/* The main container for the widget content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentWidget.id}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="w-full h-full relative z-10 rounded-3xl overflow-hidden p-4" /* Added p-4 for internal padding */
          >
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800/70 text-white text-xl font-semibold rounded-lg">
                Loading reviews...
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-red-800/70 text-white text-xl font-semibold rounded-lg">
                Error: {error}
              </div>
            )}

            {currentWidget.type === 'iframe' && (
              <iframe 
                src={currentWidget.src} 
                width="100%" 
                height={currentWidget.height} 
                frameBorder="0" 
                style={{ border: '0', display: isLoading || error ? 'none' : 'block' }}
                title={currentWidget.title}
              ></iframe>
            )}
            {currentWidget.type === 'div-embed' && (
              <div 
                id={currentWidget.htmlContainerId} 
                ref={scriptContainerRef}
                className="w-full h-full flex items-center justify-center"
                style={{ display: isLoading || error ? 'none' : 'flex' }}
              >
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      
      <div className="relative z-10 flex justify-center mt-6 gap-2">
        {/* Only render the first dot */}
        {widgets.length > 0 && (
          <button
            key={0} // Key is 0 for the first (and only) dot
            onClick={() => setCurrentWidgetIndex(0)} // Always navigate to the first widget
            className={`w-3 h-3 rounded-full ${0 === currentWidgetIndex ? 'bg-amber-500' : 'bg-gray-600'}`}
          />
        )}
      </div>
    </motion.div>
  );
};

export default TestimonialCarousel;